'use client';
import React, { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';

const techniqueCount = 847;

const floatingCards = [
{
  title: 'Balayage Freehand',
  angle: '180° Overhead',
  duration: '42 min',
  level: 'Intermediate',
  levelColor: 'badge-intermediate',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_14e1e9c88-1772070216257.png",
  alt: 'Stylist applying balayage freehand technique with brush on blonde hair',
  progress: 68,
  tag: 'Color'
},
{
  title: 'Foil Fold Precision',
  angle: '45° Side Angle',
  duration: '28 min',
  level: 'Beginner',
  levelColor: 'badge-beginner',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_17303fc1b-1772140902874.png",
  alt: 'Close-up view of foil folding technique during hair highlighting session',
  progress: 100,
  tag: 'Highlights'
},
{
  title: 'Gel Nail Architecture',
  angle: 'Macro Close-up',
  duration: '35 min',
  level: 'Advanced',
  levelColor: 'badge-advanced',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1ab55ef88-1772182366519.png",
  alt: 'Extreme close-up of gel nail art application showing brush precision on nail',
  progress: 34,
  tag: 'Nail Art'
}];


export default function HeroSection() {
  const [count, setCount] = useState(0);
  const countRef = useRef(false);

  useEffect(() => {
    if (countRef.current) return;
    countRef.current = true;
    const duration = 2000;
    const steps = 60;
    const increment = techniqueCount / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= techniqueCount) {
        setCount(techniqueCount);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, []);

  const handleDownload = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden noise-overlay">
      {/* Ambient depth layers */}
      <div className="absolute inset-0 pointer-events-none z-0">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-deep via-slate to-slate-deep" />
        {/* Blob 1 — sky */}
        <div className="blob-sky absolute top-[-10%] right-[-5%] w-[600px] h-[600px] rounded-full opacity-60" />
        {/* Blob 2 — periwinkle */}
        <div className="blob-periwinkle absolute bottom-[10%] left-[-8%] w-[500px] h-[500px] rounded-full opacity-50" />
        {/* Grid texture */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'linear-gradient(rgba(86,204,242,1) 1px, transparent 1px), linear-gradient(90deg, rgba(86,204,242,1) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }} />
        
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col justify-center max-w-7xl mx-auto w-full px-4 sm:px-6 pt-28 pb-16">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">

          {/* Left — text block */}
          <div className="flex-1 max-w-xl">
            {/* Live counter badge */}
            <div className="fade-in inline-flex items-center gap-3 glass-card px-4 py-2 rounded-full mb-8">
              <span className="w-2 h-2 rounded-full bg-sky-DEFAULT shadow-[0_0_8px_rgba(86,204,242,0.8)] animate-pulse" />
              <span className="text-xs font-semibold text-chrome tracking-wider uppercase">
                <span className="gradient-text-sky font-bold text-sm">{count.toLocaleString()}</span>
                {' '}techniques available now
              </span>
            </div>

            <h1 className="fade-in-up delay-100 font-sans text-5xl sm:text-6xl lg:text-7xl font-extrabold text-frost leading-[1.0] tracking-tight mb-6">
              Every Technique.{' '}
              <span className="font-display italic font-light gradient-text-sky">
                Every Angle.
              </span>{' '}
              <span className="block mt-1">One App.</span>
            </h1>

            <p className="fade-in-up delay-200 text-chrome text-lg sm:text-xl font-light leading-relaxed mb-10 max-w-lg">
              Camera-angle tutorials so close you can see the tension in every foil fold.
              Built for stylists who are done guessing and ready to build speed.
            </p>

            <div className="fade-in-up delay-300 flex flex-col sm:flex-row gap-4">
              <a
                href="#download"
                onClick={handleDownload}
                className="btn-primary flex items-center justify-center gap-3 px-7 py-4 rounded-full text-slate-deep font-bold text-base tracking-wide">
                
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                Download & Start Free
              </a>
              <a
                href="#curriculum"
                onClick={(e) => {e.preventDefault();document.getElementById('curriculum')?.scrollIntoView({ behavior: 'smooth' });}}
                className="glass-card glow-hover flex items-center justify-center gap-2 px-7 py-4 rounded-full text-frost font-semibold text-base tracking-wide">
                
                Explore Curriculum
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </a>
            </div>

            {/* Trust signals */}
            <div className="fade-in-up delay-400 flex items-center gap-6 mt-10">
              <div className="flex -space-x-2">
                {['https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=80', 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?q=80&w=80', 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?q=80&w=80'].map((src, i) =>
                <div key={i} className="w-8 h-8 rounded-full overflow-hidden border-2 border-slate-deep">
                    <AppImage src={src} alt={`Stylist ${i + 1} avatar`} width={32} height={32} className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center gap-1 mb-0.5">
                  {[1, 2, 3, 4, 5].map((s) =>
                  <svg key={s} className="w-3 h-3 text-sky-DEFAULT" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  )}
                </div>
                <p className="text-xs text-chrome-dim">Trusted by <span className="text-frost font-semibold">12,400+</span> stylists</p>
              </div>
            </div>
          </div>

          {/* Right — floating lesson cards */}
          <div className="flex-1 relative w-full max-w-lg lg:max-w-none h-[480px] sm:h-[520px] lg:h-[600px]">
            {floatingCards.map((card, i) => {
              const positions = [
              'top-0 left-4 sm:left-8 w-56 sm:w-64',
              'top-[15%] right-0 w-60 sm:w-72 z-20',
              'bottom-0 left-[10%] w-52 sm:w-60'];

              const floatClass = ['float-a', 'float-b', 'float-c'][i];
              const delayClass = ['delay-200', 'delay-400', 'delay-600'][i];

              return (
                <div
                  key={i}
                  className={`absolute ${positions[i]} ${floatClass} fade-in-up ${delayClass}`}>
                  
                  <div className="glass-card-bright rounded-2xl overflow-hidden glow-hover transition-all duration-500">
                    {/* Lesson image */}
                    <div className="relative h-32 sm:h-36 overflow-hidden">
                      <AppImage
                        src={card.img}
                        alt={card.alt}
                        fill
                        className="object-cover" />
                      
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-deep/80 to-transparent" />
                      {/* Camera angle badge */}
                      <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-slate-deep/70 backdrop-blur-sm px-2 py-1 rounded-full">
                        <svg className="w-3 h-3 text-sky-DEFAULT" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" />
                        </svg>
                        <span className="text-[9px] font-bold text-sky-DEFAULT tracking-wide">{card.angle}</span>
                      </div>
                      {/* Tag */}
                      <div className="absolute top-2 right-2 bg-slate-deep/70 backdrop-blur-sm px-2 py-1 rounded-full">
                        <span className="text-[9px] font-bold text-chrome tracking-wide">{card.tag}</span>
                      </div>
                    </div>

                    {/* Card body */}
                    <div className="p-3">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="text-frost text-xs font-bold leading-tight">{card.title}</h3>
                        <span className={`${card.levelColor} text-[9px] font-semibold px-2 py-0.5 rounded-full shrink-0`}>
                          {card.level}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-chrome-dim text-[10px]">{card.duration}</span>
                        {/* Progress ring */}
                        <div className="relative w-8 h-8">
                          <svg className="w-8 h-8 progress-ring" viewBox="0 0 32 32">
                            <circle cx="16" cy="16" r="12" fill="none" stroke="rgba(160,174,192,0.15)" strokeWidth="2.5" />
                            <circle
                              cx="16" cy="16" r="12" fill="none"
                              stroke={card.progress === 100 ? '#56CCF2' : '#7B61FF'}
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeDasharray={`${card.progress / 100 * 75.4} 75.4`} />
                            
                          </svg>
                          <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold text-frost">
                            {card.progress}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>);

            })}

            {/* Floating stat pill */}
            <div className="absolute bottom-[25%] right-[5%] float-b fade-in-up delay-500 glass-card px-4 py-3 rounded-xl">
              <div className="text-xs text-chrome-dim mb-0.5">New this week</div>
              <div className="text-frost font-bold text-lg gradient-text-sky">+24 lessons</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="relative z-10 flex justify-center pb-8">
        <div className="flex flex-col items-center gap-2 text-chrome-dim animate-bounce">
          <span className="text-[10px] uppercase tracking-widest font-medium">Explore</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    </section>);

}